import React, { useEffect, useRef } from "react";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { USDZExporter } from "three/examples/jsm/exporters/USDZExporter.js";
import * as THREE from "three";
import { useLocation } from "react-router-dom";
import { fetchModelByUrl } from "../api/modelApi";

/**
 * ARViewer component: Displays a 3D model and handles AR functionality based on device and model type.
 *
 * Features:
 * - iOS:
 *   - Opens AR Quick Look for USDZ models.
 *   - Converts GLB models to USDZ and opens them in AR Quick Look.
 * - Android:
 *   - Uses WebXR for immersive AR sessions if supported.
 * - Other platforms:
 *   - Renders the 3D model using Three.js fallback viewer.
 *
 * Main components:
 * - `containerRef`: Reference to the DOM container for the Three.js scene.
 * - `useLocation`: Retrieves the current location object to access query parameters.
 * - `isIOS`: Function to check if the device is an iOS device.
 * - `GLTFLoader`: The GLTFLoader is responsible for fetching the 3D model from the specified URL.
 *                 It handles the loading of the model data, which includes geometry, materials, and other assets.
 * - `OrbitControls`: Provides camera controls for interacting with the 3D scene.
 * - `USDZExporter`: Converts GLB models to USDZ format for AR on iOS devices.
 * - `THREE`: Three.js library for 3D rendering.
 * - `WebXR`: Provides immersive AR sessions for Android devices with WebXR support.
 * - Camera, lighting, and renderer setup: Configures the 3D scene for rendering.
 * - Model optimization: Adjusts model materials for better AR appearance.
 * - Animation loop: Renders the scene continuously.
 */

const ARViewer = () => {
  const containerRef = useRef(null);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const modelURL = queryParams.get("model");
  const type = queryParams.get("type");

  const isIOS = () =>
    /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  const isAndroid = () => /Android/.test(navigator.userAgent);

  const handleIOSUSDZ = (modelBlobURL) => {
    console.log("iOS detected, displaying USDZ model:", modelBlobURL);
    window.location.href = modelBlobURL;
  };

  const handleIOSGLB = (modelBlobURL) => {
    console.log("iOS detected. Converting .glb to .usdz...");
    const loader = new GLTFLoader();
    loader.load(
      modelBlobURL,
      (gltf) => {
        const model = gltf.scene;

        model.traverse((node) => {
          if (node.isMesh && node.material) {
            node.material = node.material.clone();
            node.material.metalness = 0.1;
            node.material.roughness = 0.4;
            node.material.color.convertSRGBToLinear();
            node.material.emissive = new THREE.Color(0x000000);
            node.material.envMapIntensity = 1.5;
          }
        });

        const exporter = new USDZExporter();
        exporter.parse(model, (usdz) => {
          const usdzBlob = new Blob([usdz], {
            type: "model/vnd.usdz+zip",
          });
          const usdzURL = URL.createObjectURL(usdzBlob);
          console.log("Converted USDZ URL:", usdzURL);
          window.location.href = usdzURL;
        });
      },
      undefined,
      (error) => {
        console.error("Error loading .glb model for conversion:", error);
        alert("Failed to load the 3D model for AR.");
      },
    );
  };

  const handleAndroidWebXR = (modelBlobURL) => {
    navigator.xr.isSessionSupported("immersive-ar").then((supported) => {
      if (supported) {
        console.log("WebXR supported. Launching immersive AR session...");
        navigator.xr
          .requestSession("immersive-ar", {
            requiredFeatures: ["local", "hit-test"],
          })
          .then((xrSession) => initializeWebXRSession(xrSession))
          .catch((error) => {
            console.error("Error starting AR session:", error);
            setupFallbackViewer(modelBlobURL);
          });
      } else {
        console.warn(
          "WebXR not supported on this device. Falling back to Three.js viewer.",
        );
        setupFallbackViewer(modelBlobURL);
      }
    });
  };

  const initializeWebXRSession = (xrSession) => {
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera();
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.xr.enabled = true;
    containerRef.current.appendChild(renderer.domElement);

    const loader = new GLTFLoader();
    loader.load(
      modelURL,
      (gltf) => {
        const model = gltf.scene;

        model.scale.set(1, 1, 1);
        scene.add(model);

        // Add lighting
        const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
        scene.add(ambientLight);

        const directionalLight = new THREE.DirectionalLight(0xffffff, 1.2);
        directionalLight.position.set(0, 5, 5);
        scene.add(directionalLight);

        // Render loop for WebXR
        const onSessionEnd = () => {
          xrSession.end();
          console.log("WebXR session ended.");
        };

        xrSession.addEventListener("end", onSessionEnd);

        const animate = () => {
          renderer.setAnimationLoop(() => {
            renderer.render(scene, camera);
          });
        };

        animate();
      },
      undefined,
      (error) => {
        console.error("Error loading model for WebXR:", error);
      },
    );
  };

  const setupFallbackViewer = (blobUrl) => {
    const container = containerRef.current;
    const scene = new THREE.Scene();

    // Load background texture as a sphere for environment
    const textureLoader = new THREE.TextureLoader();
    textureLoader.load("hd-table-background.webp", (texture) => {
      texture.mapping = THREE.EquirectangularReflectionMapping; // Ensures proper spherical mapping
      scene.background = texture;
    });

    // Camera setup
    const camera = new THREE.PerspectiveCamera(
      60,
      window.innerWidth / window.innerHeight,
      0.1,
      1000,
    );

    // Renderer setup
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(window.innerWidth, window.innerHeight);
    container.appendChild(renderer.domElement);

    // Lighting setup
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 1.2);
    directionalLight.position.set(2, 5, 5);
    scene.add(directionalLight);

    // OrbitControls setup
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.enablePan = false; // Disable panning
    controls.enableRotate = true; // Allow rotation
    controls.enableZoom = false; // Disable zooming

    // Restrict movement
    controls.minPolarAngle = Math.PI / 4; // Prevent looking straight up
    controls.maxPolarAngle = Math.PI / 2; // Prevent looking under the table

    // Load model
    const loader = new GLTFLoader();
    loader.load(
      blobUrl,
      (gltf) => {
        const model = gltf.scene;

        // Center the model
        const box = new THREE.Box3().setFromObject(model);
        const size = box.getSize(new THREE.Vector3());
        const center = box.getCenter(new THREE.Vector3());

        model.position.set(
          -center.x + 7 * size.x,
          -center.y - 50 * size.y,
          -center.z,
        );
        model.scale.set(6, 6, 6);
        scene.add(model);

        // Camera setup
        camera.position.set(0, 0, 0);
        camera.lookAt(0, 0, 0);

        // Set controls to focus on the model
        controls.target.set(-center.x + 0.7, -center.y - 0.7, -center.z - 0.1);
        controls.update();
      },
      undefined,
      (error) => {
        console.error("Error loading model for fallback viewer:", error);
      },
    );

    const animate = () => {
      requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };

    animate();
  };

  useEffect(() => {
    if (!modelURL) {
      console.error("Model URL is missing or invalid.");
      return;
    }

    let modelBlobURL;

    const fetchAndLoadModel = async () => {
      try {
        console.log("Fetching model from backend...");
        const modelBlob = await fetchModelByUrl(modelURL);
        const modelBlobURL = URL.createObjectURL(modelBlob);

        if (isIOS() && type === "usdz") {
          handleIOSUSDZ(modelBlobURL);
        } else if (isIOS() && type === "glb") {
          handleIOSGLB(modelBlobURL);
        } else if (
          isAndroid() &&
          navigator.xr &&
          navigator.xr.isSessionSupported
        ) {
          handleAndroidWebXR(modelBlobURL);
        } else {
          setupFallbackViewer(modelBlobURL);
        }
      } catch (error) {
        console.error("Error fetching or processing model:", error);
        alert("Failed to fetch the 3D model from the backend.");
      }
    };

    fetchAndLoadModel();
    return () => {
      if (modelBlobURL) {
        URL.revokeObjectURL(modelBlobURL);
        console.log("Revoked object URL:", modelBlobURL);
      }
      while (containerRef.current.firstChild) {
        containerRef.current.removeChild(containerRef.current.firstChild);
      }
    };
  }, [modelURL, type]);

  return <div ref={containerRef} style={{ width: "100%", height: "100vh" }} />;
};

export default ARViewer;
